import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { listDevices, listPatients } from '../lib/adapters';
import { batteryColor, signalLabel } from '../lib/devices';
import { loadDeviceCatalog, deviceLabelFrom, deviceIconFrom, categoriesFrom, categoryLabel } from '../lib/deviceCatalog';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { StatusChip } from '../components/StatusChip';
import { useRelativeTime } from '../lib/time';
import { ArrowRight } from '@phosphor-icons/react';

export default function Devices() {
  const { t } = useI18n();
  const rel = useRelativeTime();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('type') || 'all');
  const [devices, setDevices] = useState([]);
  const [patients, setPatients] = useState({});
  const [catalog, setCatalog] = useState([]);

  useEffect(() => { loadDeviceCatalog().then(setCatalog).catch(() => setCatalog([])); }, []);
  // The gateway ("Bluetooth gateway") filter is admin/super-admin only.
  const tabs = ['all', ...categoriesFrom(catalog).filter((c) => isAdmin || c !== 'gateway')];

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'all') next.delete('type'); else next.set('type', tab);
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line
  }, [tab]);

  useEffect(() => {
    listPatients().then(({ items }) => {
      const map = {};
      items.forEach((p) => { map[p.id] = p; });
      setPatients(map);
    });
  }, []);

  useEffect(() => {
    // Category filter goes to the backend as-is; it expands categories server-side.
    // Non-admins can't filter by gateway, so coerce that selection back to "all".
    const effectiveTab = (!isAdmin && tab === 'gateway') ? 'all' : tab;
    listDevices({ type: effectiveTab === 'all' ? undefined : effectiveTab }).then(({ items }) => setDevices(items));
  }, [tab]);

  return (
    <div className="space-y-8">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-3">
          {t('nav_devices')}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl text-ink leading-none">{t('devices_title')}</h1>
        <p className="text-ink-soft mt-3 max-w-xl">
          {isAdmin ? t('devices_sub_admin') : t('devices_sub')}
        </p>
      </header>

      <div className="flex gap-2 bg-white border border-stone_line rounded-xl p-1 w-fit" data-testid="devices-tabs">
        {tabs.map((x) => (
          <button
            key={x}
            onClick={() => setTab(x)}
            data-testid={`devices-tab-${x}`}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              tab === x ? 'bg-sage text-white' : 'text-ink-soft hover:bg-bg-alt'
            }`}
          >
            {x === 'all' ? t('all') : categoryLabel(x)}
          </button>
        ))}
      </div>

      <div className="bg-white border border-stone_line rounded-2xl shadow-soft overflow-hidden">
        <div className="hidden md:grid grid-cols-[1.4fr_1fr_1fr_0.8fr_0.8fr_0.8fr_0.8fr] gap-4 px-6 py-3 border-b border-stone_line bg-bg-alt text-[10px] uppercase tracking-[0.18em] text-ink-muted font-semibold">
          <div>{t('nav_devices')}</div>
          <div>{isAdmin ? 'MAC' : t('nav_patients')}</div>
          <div>{isAdmin ? t('nav_patients') : t('battery')}</div>
          <div>{t('battery')}</div>
          <div>{t('device_signal')}</div>
          <div>{t('device_ota_status')}</div>
          <div>{t('last_seen')}</div>
        </div>
        {devices.length === 0 ? (
          <div className="p-10 text-center text-ink-muted">{t('no_results')}</div>
        ) : (
          <ul className="divide-y divide-stone_line" data-testid="devices-list">
            {devices.map((d) => {
              const Icon = deviceIconFrom(catalog, d.type);
              const owner = patients[d.resident_id];
              const sig = signalLabel(d.signal_quality);
              return (
                <li key={d.id}>
                  <Link
                    to={`/devices/${d.id}`}
                    data-testid={`device-row-${d.id}`}
                    className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1fr_0.8fr_0.8fr_0.8fr_0.8fr] gap-4 px-6 py-4 items-center hover:bg-bg-alt/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-sage-100 text-sage-600 grid place-items-center">
                        <Icon size={18} weight="duotone" />
                      </div>
                      <div>
                        <div className="text-sm text-ink font-medium">
                          {deviceLabelFrom(catalog, d.type)}
                        </div>
                        <div className="mt-0.5"><StatusChip status={d.connectivity_status} /></div>
                      </div>
                    </div>
                    <div className="text-xs font-mono text-ink-soft truncate">{isAdmin ? d.mac_address : (owner?.name || '—')}</div>
                    <div className="text-sm text-ink truncate">{isAdmin ? (owner?.name || '—') : `${d.battery}%`}</div>
                    <div className={`text-sm ${batteryColor(d.battery)}`}>{d.battery}%</div>
                    <div className={`text-xs ${sig.cls}`}>{sig.label}</div>
                    <div>
                      <span className="text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-1 rounded-full bg-bg-alt text-ink-soft">
                        {t(`ota_${d.ota_status}`)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-ink-soft">{rel(d.last_seen)}</span>
                      <ArrowRight size={14} className="text-ink-muted" />
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
